import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/motion/ScrollReveal";

/**
 * CountdownSection — section-scale "countdown to flag-off" block.
 *
 * Sits on its own band between the When/Where/Time essentials and the StoryIntro
 * narrative. Unlike the inline `CountdownBadge` (a hero eyebrow), this renders as
 * a deliberate focal moment: an eyebrow label + four big Bebas Neue number tiles
 * (Days / Hrs / Min / Sec) in the runner accent palette, ticking live.
 *
 * Ticks every second so the seconds tile is genuinely live; equal grid columns +
 * `tabular-nums` keep the numerals from shifting layout as they change.
 *
 * Accessibility: the ticking grid is `aria-hidden` (announcing every second would
 * spam assistive tech); a concise `sr-only` line carries the date instead.
 */

type Props = {
  /** ISO date/time of the countdown target. */
  targetDate?: string;
  /** Human-readable date shown beneath the tiles and to screen readers. */
  eventDateLabel?: string;
  /** Eyebrow label above the tiles. Defaults to the flag-off copy. */
  eyebrow?: string;
  /** Override styling for the eyebrow label (e.g. to promote it to a large highlight). */
  eyebrowClassName?: string;
  /** Message shown once the target passes. Defaults to the flag-off "race day" copy. */
  completedLabel?: string;
  /** DOM id for the heading — must be unique if the section is rendered more than once. */
  headingId?: string;
  /** i18n key for the screen-reader description sentence. */
  srDescriptionKey?: string;
};

type Parts = { days: number; hours: number; minutes: number; seconds: number };

function getParts(targetMs: number, nowMs: number): Parts | null {
  const diff = targetMs - nowMs;
  if (diff <= 0) return null;
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

export function CountdownSection({
  targetDate = "2026-11-28T00:00:00+08:00",
  eventDateLabel,
  eyebrow,
  eyebrowClassName = "text-xs uppercase tracking-[0.28em] text-primary font-semibold",
  completedLabel,
  headingId = "countdown-heading",
  srDescriptionKey = "countdownSection.srDescription"
}: Props) {
  const { t } = useTranslation("home");
  const resolvedDateLabel = eventDateLabel ?? t("countdownSection.eventDateLabel");
  const resolvedEyebrow = eyebrow ?? t("countdownSection.eyebrow");
  const resolvedCompletedLabel = completedLabel ?? t("countdownSection.raceDay");
  const targetMs = new Date(targetDate).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);

  const parts = getParts(targetMs, now);

  // Runner-palette accents, one per unit — mirrors the KeyDetails card tones.
  const units = parts
    ? [
        { key: "days", label: t("countdownSection.units.days"), value: String(parts.days), tone: "text-accent-teal" },
        { key: "hours", label: t("countdownSection.units.hours"), value: pad(parts.hours), tone: "text-accent-orange" },
        { key: "minutes", label: t("countdownSection.units.minutes"), value: pad(parts.minutes), tone: "text-accent-magenta" },
        { key: "seconds", label: t("countdownSection.units.seconds"), value: pad(parts.seconds), tone: "text-accent-violet" }
      ]
    : [];

  return (
    <section aria-labelledby={headingId} className="bg-background">
      <div className="container py-16 md:py-20">
        <ScrollReveal>
          <div className="flex flex-col items-center text-center">
            <p id={headingId} className={eyebrowClassName}>
              {resolvedEyebrow}
            </p>

            {/* Concise, non-ticking description for assistive tech. */}
            <span className="sr-only">
              {t(srDescriptionKey, { eventDateLabel: resolvedDateLabel })}
            </span>

            {parts ? (
              <div
                aria-hidden
                className="mt-6 grid w-full max-w-xl grid-cols-4 gap-3 sm:gap-5 md:max-w-2xl"
              >
                {units.map((unit) => (
                  <div
                    key={unit.key}
                    className="flex flex-col items-center rounded-xl border border-border bg-card px-2 py-4 shadow-sm sm:px-4 sm:py-6"
                  >
                    <span
                      className={`font-display text-4xl leading-none tabular-nums sm:text-6xl md:text-7xl ${unit.tone}`}
                    >
                      {unit.value}
                    </span>
                    <span className="mt-1 text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground sm:text-xs">
                      {unit.label}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-6 font-display text-4xl tracking-normal text-primary sm:text-6xl">
                {resolvedCompletedLabel}
              </p>
            )}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
